import { useEffect, useState } from 'react';

import {
  APOLLO_CLIENTS,
  CustomContext,
  initializeApollo,
} from '@/graphql/client';
import type {
  OrderAppProductVariantBusinessDetailsQuery,
  OrderAppProductVariantBusinessDetailsQueryVariables,
} from '@/graphql/orderApp/api';
import { ProductVariantBusinessDetailsDocument } from '@/graphql/orderApp/api';
import type {
  ProductCardsQuery,
  ProductVariantWatchBasicDetailsQuery,
  ProductVariantWatchDetailsQuery,
  ProductVariantWatchDetailsQueryVariables,
} from '@/graphql/saleor/api';
import { ProductVariantWatchDetailsDocument } from '@/graphql/saleor/api';
import { getProductType } from '@/lib/product';
import { getGqlRegionFromLocale } from '@/lib/regions';

export const isValidVariant = (data: ProductVariantWatchBasicDetailsQuery) => {
  const productType = getProductType(data?.productVariant?.product.productType);
  const isWatch = productType === 'watch';
  const hasCollections = !!data?.productVariant?.product.collections?.length;

  return isWatch && hasCollections;
};

export const areParamsValid = (
  collection: string,
  model: string,
  data: ProductVariantWatchBasicDetailsQuery,
) => {
  const isProductModelCorrect =
    (data?.productVariant?.product.model?.values[0]?.slug ?? 'unknown') ===
    model;
  const productCollectionSlug = data?.productVariant?.product.collections?.find(
    ({ slug }) => slug.toLowerCase() === collection,
  )?.slug;

  return isProductModelCorrect && !!productCollectionSlug;
};

type ProductData = Partial<{
  businessData: OrderAppProductVariantBusinessDetailsQuery | undefined;
  compatibleStrapsData: ProductCardsQuery | undefined;
  recentlyViewedData: ProductCardsQuery | undefined;
  variantDetailsData: ProductVariantWatchDetailsQuery | undefined;
}>;
export const useFetchProductData = (
  sku: string,
  locale: string,
  skip: boolean,
) => {
  const [data, setData] = useState<ProductData>({
    businessData: undefined,
    variantDetailsData: undefined,
  });
  const updateData = (partialData: ProductData) =>
    setData((data) => ({ ...data, ...partialData }));

  useEffect(() => {
    if (!skip) {
      (async () => {
        const regionVariables = getGqlRegionFromLocale(locale);
        const client = initializeApollo(); //secure query can not use here, it only use in ssr

        const [{ data: variantDetailsData }, { data: businessData }] =
          await Promise.all([
            client.query<
              ProductVariantWatchDetailsQuery,
              ProductVariantWatchDetailsQueryVariables
            >({
              query: ProductVariantWatchDetailsDocument,
              variables: {
                sku: sku.toUpperCase(),
                channel: regionVariables.channel,
                languageCode: regionVariables.languageCode,
              },
            }),

            client.query<
              OrderAppProductVariantBusinessDetailsQuery,
              OrderAppProductVariantBusinessDetailsQueryVariables
            >({
              query: ProductVariantBusinessDetailsDocument,
              variables: {
                sku,
                channel: regionVariables.channel,
              },
              context: CustomContext({
                client: APOLLO_CLIENTS.orderApp,
                disableErrorNotification: true,
              }),
            }),
          ]);

        updateData({ variantDetailsData, businessData });
      })();
    }
  }, [sku, locale, skip]);

  return data;
};
